import { formatInTimeZone } from 'date-fns-tz'
import { APP_TIME_ZONE, dayRange, type DayKey } from './time'

/**
 * The step a drag lands on when there is nothing finer to be had. Five minutes
 * is the right default: second-level precision is not the point, and it keeps a
 * casual drag on a round number.
 */
export const SNAP_MS = 5 * 60 * 1000

/** The finest a drag ever goes, when the pixels or a held key allow it. */
export const FINE_SNAP_MS = 60 * 1000

/**
 * A step you cannot reliably land on is not a step. Below this many pixels the
 * next one up is used instead, which is what ties precision to zoom.
 */
const MIN_SNAP_PX = 3

export const MIN_PX_PER_HOUR = 26

/**
 * A gap thinner than this on screen cannot realistically be dragged across, so a
 * plain press in one fills it instead of doing nothing. Measured in pixels
 * rather than minutes, so zooming in turns a fiddly gap into a draggable one.
 */
export const TAP_FILL_PX = 26

const HOUR_MS = 60 * 60 * 1000

export interface DayGeometry {
  key: DayKey
  /** Epoch ms of the logical day's first and last instant. */
  start: number
  end: number
  /** Real elapsed length — 23, 24 or 25 hours. */
  lengthMs: number
  pxPerHour: number
  /** Column height in px, so a 25-hour day is simply taller. */
  height: number
}

export function dayGeometry(
  key: DayKey,
  dayStartHour: number,
  pxPerHour: number,
  timeZone: string = APP_TIME_ZONE,
): DayGeometry {
  const { start, end } = dayRange(key, dayStartHour, timeZone)
  const lengthMs = end.getTime() - start.getTime()
  return {
    key,
    start: start.getTime(),
    end: end.getTime(),
    lengthMs,
    pxPerHour,
    height: (lengthMs / HOUR_MS) * pxPerHour,
  }
}

/**
 * Positions are elapsed hours from the day start, not wall-clock hours, so the
 * repeated hour on a fall-back day gets its own band instead of overlapping the
 * hour it repeats.
 */
export function yForInstant(geo: DayGeometry, instant: number): number {
  return ((instant - geo.start) / HOUR_MS) * geo.pxPerHour
}

export function instantForY(geo: DayGeometry, y: number): number {
  const raw = geo.start + (y / geo.pxPerHour) * HOUR_MS
  return Math.min(geo.end, Math.max(geo.start, raw))
}

export function snapMs(instant: number, snap: number = SNAP_MS): number {
  return Math.round(instant / snap) * snap
}

/**
 * How fine a drag can be at this scale.
 *
 * Precision comes from the pixels available, not from a fixed number of
 * minutes: zoomed out, a minute is a pixel and snapping to it would be a lie,
 * so drags land on five; zoom in far enough that a minute is a few pixels tall
 * and they land on one. `fine` — a held modifier — asks for the minute anyway,
 * for when zooming in is more trouble than the precision is worth.
 */
export function snapForPxPerHour(pxPerHour: number, fine = false): number {
  if (fine) return FINE_SNAP_MS
  const minutePx = pxPerHour / 60
  return minutePx >= MIN_SNAP_PX ? FINE_SNAP_MS : SNAP_MS
}

/** The gestures a timeline drag can be, which is what decides how fine it goes. */
export type DragMode = 'create' | 'move' | 'resize-start' | 'resize-end' | 'boundary'

/**
 * Whether a gesture works to the minute whatever the scale.
 *
 * Dragging an edge or a seam is a claim about one instant — "it ended at 11:07"
 * — and grabbing a nine-pixel handle is already a deliberate act, so those
 * always do. Moving a block or painting a new one is a claim about roughly
 * where something sits, so they keep the round default until the pixels or a
 * held key say otherwise.
 */
export function fineDrag(mode: DragMode, alt: boolean): boolean {
  return alt || mode === 'resize-start' || mode === 'resize-end' || mode === 'boundary'
}

/** Snapped and clamped inside the day — what a drag gesture should commit. */
export function instantForYSnapped(geo: DayGeometry, y: number, snap: number = SNAP_MS): number {
  return Math.min(geo.end, Math.max(geo.start, snapMs(instantForY(geo, y), snap)))
}

export interface HourTick {
  instant: number
  y: number
  /** Wall-clock label; a 25-hour day legitimately shows the same one twice. */
  label: string
  hour: number
}

export function hourTicks(geo: DayGeometry, timeZone: string = APP_TIME_ZONE): HourTick[] {
  const ticks: HourTick[] = []
  for (let instant = geo.start; instant < geo.end; instant += HOUR_MS) {
    const label = formatInTimeZone(new Date(instant), timeZone, 'HH:mm')
    ticks.push({
      instant,
      y: yForInstant(geo, instant),
      label,
      hour: Number(label.slice(0, 2)),
    })
  }
  return ticks
}

/**
 * Scroll offset that puts a wall-clock hour at the top of the viewport. Returns
 * the first matching tick, so on a fall-back day the earlier of the two repeated
 * hours wins.
 */
export function offsetForWallHour(geo: DayGeometry, hour: number, timeZone: string = APP_TIME_ZONE): number {
  const tick = hourTicks(geo, timeZone).find((candidate) => candidate.hour === hour)
  return tick ? tick.y : 0
}

/**
 * Pixels per hour that make the working window exactly fill the viewport. Every
 * other hour of the day stays reachable by scrolling.
 */
export function pxPerHourForWindow(viewportHeight: number, fromHour: number, toHour: number): number {
  const hours = Math.max(1, toHour - fromHour)
  return Math.max(MIN_PX_PER_HOUR, viewportHeight / hours)
}

/** The free stretch around `instant`, bounded by whatever blocks sit either side. */
export function gapAround(
  instant: number,
  blocks: Array<{ start: number; end: number | null }>,
  geo: DayGeometry,
): { from: number; to: number } | null {
  let from = geo.start
  let to = geo.end
  for (const block of blocks) {
    const blockEnd = block.end ?? geo.end
    if (blockEnd <= instant) from = Math.max(from, blockEnd)
    else if (block.start >= instant) to = Math.min(to, block.start)
    else return null // the instant is inside a block
  }
  return from < to ? { from, to } : null
}

/** Whether a press in this gap should fill it outright rather than start a drag. */
export function tapFillsGap(
  gap: { from: number; to: number },
  pxPerHour: number,
  thresholdPx: number = TAP_FILL_PX,
): boolean {
  const length = gap.to - gap.from
  // A gap shorter than the finest step is not worth a block.
  if (length < FINE_SNAP_MS) return false
  return (length / HOUR_MS) * pxPerHour <= thresholdPx
}

/** Every free stretch in the day, ignoring one block — the one being dragged. */
export function freeGaps(
  blocks: Array<{ id: string; start: number; end: number | null }>,
  geo: { start: number; end: number },
  exceptId: string,
): Array<{ from: number; to: number }> {
  const occupied = blocks
    .filter((block) => block.id !== exceptId)
    .map((block) => ({ start: block.start, end: block.end ?? geo.end }))
    .sort((a, b) => a.start - b.start)

  const gaps: Array<{ from: number; to: number }> = []
  let cursor = geo.start
  for (const block of occupied) {
    if (block.start > cursor) gaps.push({ from: cursor, to: block.start })
    cursor = Math.max(cursor, block.end)
  }
  if (cursor < geo.end) gaps.push({ from: cursor, to: geo.end })
  return gaps
}

/**
 * Where a block of `length` should sit when the pointer is over `instant`.
 *
 * A drag stops when it meets a neighbour, but it does not get stuck there: move
 * the pointer into a gap on the far side that the block fits in and it goes,
 * rather than pinning itself to the first obstacle it met. `preferred` is the
 * start the pointer asks for; the return is the nearest start inside a gap that
 * can hold it, or null when the pointer is nowhere one can.
 */
export function placeInGaps(
  gaps: Array<{ from: number; to: number }>,
  instant: number,
  preferred: number,
  length: number,
): number | null {
  const fitting = gaps.filter((gap) => gap.to - gap.from >= length)
  if (fitting.length === 0) return null

  // The gap the pointer is actually over wins, so the block follows the mouse
  // across an obstacle instead of sliding to whichever gap is mathematically
  // closest to where it started.
  const under = fitting.find((gap) => instant >= gap.from && instant <= gap.to)
  const gap =
    under ??
    fitting.reduce((best, candidate) =>
      distanceTo(candidate, instant) < distanceTo(best, instant) ? candidate : best,
    )
  return Math.min(gap.to - length, Math.max(gap.from, preferred))
}

function distanceTo(gap: { from: number; to: number }, instant: number): number {
  if (instant < gap.from) return gap.from - instant
  if (instant > gap.to) return instant - gap.to
  return 0
}
